// Mobile Orders List Page
// Mobile version (<768px)

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderApi } from '@/services/orderApi';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav, { type NavItem } from '@/components/mobile/MobileBottomNav';
import type { Order, OrderItem } from '@/types';
import styles from './MobileOrdersListPage.module.css';

type StatusFilter = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: '全部',
  pending: '待處理',
  processing: '處理中',
  shipped: '已出貨',
  delivered: '已送達',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#ff9800',
  processing: '#2196f3',
  shipped: '#9c27b0',
  delivered: '#4caf50',
  cancelled: '#f44336',
  failed: '#f44336',
};

export default function MobileOrdersListPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const loadOrders = async (reset = false) => {
    if (reset) {
      setPage(0);
      setOrders([]);
      setHasMore(true);
    }

    const currentPage = reset ? 0 : page;
    setIsLoading(true);
    setError(null);

    try {
      const response = await orderApi.getOrders({
        limit: 10,
        offset: currentPage * 10,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });

      if (reset) {
        setOrders(response.data);
      } else {
        setOrders((prev) => [...prev, ...response.data]);
      }

      setHasMore(response.data.length === 10);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入訂單失敗');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(true);
  }, [statusFilter]);

  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prev) => prev + 1);
      loadOrders();
    }
  };

  const handleCancelOrder = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('確定要取消此訂單嗎？')) {
      return;
    }

    try {
      await orderApi.cancelOrder(orderId);
      loadOrders(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '取消訂單失敗');
    }
  };

  // Nav items for bottom navigation
  const navItems: NavItem[] = [
    {
      id: 'home',
      label: '首頁',
      path: '/',
      icon: '🏠',
    },
    {
      id: 'ads',
      label: '廣告',
      path: '/ads',
      icon: '🛒',
    },
    {
      id: 'wish',
      label: '願望',
      path: '/wishes',
      icon: '⭐',
    },
    {
      id: 'profile',
      label: '會員',
      path: '/member',
      icon: '👤',
    },
  ];

  return (
    <div className={styles.ordersPage}>
      <MobileHeader
        title="我的訂單"
        showBack={false}
      />

      {/* Filter */}
      <div className={styles.header}>
        <div className={styles.filter}>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value as StatusFilter)}
              className={`${styles.filterButton} ${statusFilter === value ? styles.filterButtonActive : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)} className={styles.closeButton}>×</button>
        </div>
      )}

      {/* Orders List */}
      {orders.length === 0 && !isLoading ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📦</div>
          <p className={styles.emptyMessage}>沒有訂單記錄</p>
          <button onClick={() => navigate('/products')} className={styles.shopButton}>
            前往購物
          </button>
        </div>
      ) : (
        <div className={styles.ordersList}>
          {orders.map((order) => (
            <div
              key={order.id}
              className={styles.orderCard}
              onClick={() => navigate(`/orders/${order.id}`)}
            >
              <div className={styles.orderHeader}>
                <div>
                  <span className={styles.orderNumber}>{order.orderNumber}</span>
                  <span className={styles.orderDate}>{formatDate(order.createdAt)}</span>
                </div>
                <span
                  className={styles.orderStatus}
                  style={{ color: STATUS_COLORS[order.status] || '#666' }}
                >
                  {STATUS_LABELS[order.status as StatusFilter] || order.status}
                </span>
              </div>

              <div className={styles.orderItems}>
                {order.items.slice(0, 2).map((item: OrderItem) => (
                  <div key={item.id} className={styles.orderItem}>
                    <span className={styles.itemName}>{item.productSnapshot.title}</span>
                    <span className={styles.itemQty}>×{item.quantity}</span>
                  </div>
                ))}
                {order.items.length > 2 && (
                  <span className={styles.moreItems}>
                    還有 {order.items.length - 2} 件商品
                  </span>
                )}
              </div>

              <div className={styles.orderFooter}>
                <span className={styles.orderTotal}>{formatPrice(order.total)}</span>
                {order.status === 'pending' && (
                  <button
                    onClick={(e) => handleCancelOrder(order.id, e)}
                    className={styles.cancelButton}
                  >
                    取消
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Load More */}
          {hasMore && orders.length > 0 && (
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className={styles.loadMoreButton}
            >
              {isLoading ? '載入中...' : '載入更多'}
            </button>
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav items={navItems} />
    </div>
  );
}
